/**
 * GTIN handling. Scanners lie: ML Kit reports UPC-A as EAN-13 with a leading
 * zero, curved cans produce checksum-passing misreads, and USDA's API only
 * matches barcodes zero-padded to 14 digits. Everything is canonicalised to a
 * 13-digit GTIN before it is stored or looked up.
 */

export type BarcodeSymbology =
  | 'ean13'
  | 'ean8'
  | 'upc_a'
  | 'upc_e'
  | 'itf14'
  | 'code128'
  | 'datamatrix'
  | 'qr'

const DIGITS_ONLY = /^\d+$/

/**
 * GS1 mod-10 check digit: weight digits 3,1,3,1… from the right (excluding the
 * check digit itself), then take the complement to the next multiple of ten.
 */
export function gtinCheckDigit(digitsWithoutCheck: string): number {
  let sum = 0
  for (
    let i = digitsWithoutCheck.length - 1, weight = 3;
    i >= 0;
    i--, weight = weight === 3 ? 1 : 3
  ) {
    sum += Number(digitsWithoutCheck[i]) * weight
  }
  return (10 - (sum % 10)) % 10
}

/** Validate any fixed-length GTIN (8, 12, 13 or 14 digits). */
export function isValidGtin(code: string): boolean {
  if (!DIGITS_ONLY.test(code)) return false
  if (![8, 12, 13, 14].includes(code.length)) return false
  const body = code.slice(0, -1)
  const check = Number(code.slice(-1))
  return gtinCheckDigit(body) === check
}

/** Expand a zero-suppressed UPC-E code to its 12-digit UPC-A equivalent. */
export function upcEToUpcA(upcE: string): string | null {
  const raw = upcE.length === 8 ? upcE.slice(1, 7) : upcE.length === 6 ? upcE : null
  if (raw == null || !DIGITS_ONLY.test(raw)) return null
  const numberSystem = upcE.length === 8 ? upcE[0]! : '0'
  if (numberSystem !== '0' && numberSystem !== '1') return null

  const [d1, d2, d3, d4, d5, d6] = raw.split('') as [string, string, string, string, string, string]
  let body: string
  switch (d6) {
    case '0':
    case '1':
    case '2':
      body = `${d1}${d2}${d6}0000${d3}${d4}${d5}`
      break
    case '3':
      body = `${d1}${d2}${d3}00000${d4}${d5}`
      break
    case '4':
      body = `${d1}${d2}${d3}${d4}00000${d5}`
      break
    default:
      body = `${d1}${d2}${d3}${d4}${d5}0000${d6}`
      break
  }
  const withSystem = `${numberSystem}${body}`
  const check = gtinCheckDigit(withSystem)
  // The check digit printed on the symbol is the whole point of having one.
  // Computing a fresh one and discarding what was scanned made every 8-digit
  // string expand "successfully", so a misread digit off a curved can became a
  // confident lookup of somebody else's product instead of a rejected frame.
  if (upcE.length === 8 && Number(upcE[7]) !== check) return null
  return `${withSystem}${check}`
}

/**
 * Canonical storage form: 13 digits. UPC-A gains a leading zero, EAN-8 stays 8
 * (it is a distinct code, not a truncated EAN-13), GTIN-14 keeps its packaging
 * indicator only when that indicator is non-zero.
 */
export function toCanonicalGtin(rawCode: string, symbology?: BarcodeSymbology): string | null {
  const code = rawCode.trim()
  if (!DIGITS_ONLY.test(code)) return null

  // A leading zero is only a hint, and it is one EAN-8 shares. When the scanner
  // named the symbology, believe it; when it did not, a code that validates as
  // EAN-8 is an EAN-8, and only what is left over is worth expanding. Expanding
  // on the hint alone turned valid EAN-8s like 02345673 into a fabricated
  // 13-digit code for an unrelated product, which was then looked up and bound.
  const isUpcE =
    symbology === 'upc_e' || (symbology == null && code.length === 8 && !isValidGtin(code))
  if (isUpcE) {
    const upcA = upcEToUpcA(code)
    if (upcA && isValidGtin(upcA)) return `0${upcA}`
  }
  if (code.length === 8) return isValidGtin(code) ? code : null
  if (code.length === 12) return isValidGtin(code) ? `0${code}` : null
  if (code.length === 13) return isValidGtin(code) ? code : null
  if (code.length === 14) {
    if (!isValidGtin(code)) return null
    // A leading zero means it is really an EAN-13 in a 14-digit field.
    return code.startsWith('0') ? code.slice(1) : code
  }
  return null
}

/** USDA FoodData Central matches its stored `gtinUpc` only in 14-digit form. */
export function toGtin14(canonical: string): string {
  return canonical.padStart(14, '0')
}

/**
 * Open Food Facts keys products by the barcode as printed (8, 12 or 13 digits).
 *
 * A UPC-A is stored here as 13 digits with the padding zero this module added;
 * OFF knows it by the 12 printed on the tin. The strip used to ask for 13
 * digits *after* the zeros it consumed, which no canonical code can satisfy —
 * so every North American product was queried in a form OFF does not hold and
 * came back as a miss.
 */
export function toOpenFoodFactsCode(canonical: string): string {
  return canonical.replace(/^0(?=\d{12}$)/, '')
}

const SYMBOLOGY_BY_FORMAT: Record<string, BarcodeSymbology> = {
  ean_13: 'ean13',
  ean_8: 'ean8',
  upc_a: 'upc_a',
  upc_e: 'upc_e',
  itf: 'itf14',
  code_128: 'code128',
  data_matrix: 'datamatrix',
  qr_code: 'qr',
}

/** Map a Barcode Detection API `format` string onto our stored symbology. */
export function symbologyFromDetectedFormat(format: string): BarcodeSymbology | null {
  return SYMBOLOGY_BY_FORMAT[format] ?? null
}

/** True for symbologies whose value must satisfy the GS1 check digit. */
export function requiresChecksum(symbology: BarcodeSymbology): boolean {
  return (
    symbology === 'ean13' ||
    symbology === 'ean8' ||
    symbology === 'upc_a' ||
    symbology === 'upc_e' ||
    symbology === 'itf14'
  )
}

/**
 * EAN-13 prefixes 20–29 are in-store codes: a supermarket's own weight/price
 * label, meaningless outside that shop, so they must never be bound to a food
 * or sent to Open Food Facts.
 */
export function isInStoreCode(canonical: string): boolean {
  return canonical.length === 13 && /^2[0-9]/.test(canonical)
}
